/**
 * Report share utilities — Email + WhatsApp.
 *
 * Both use URL-scheme handoffs (no backend). The PDF/CSV is downloaded
 * separately by the user's browser; the email or WhatsApp message has
 * the summary text. The user attaches the file from their downloads
 * folder.
 *
 * mailto and wa.me have URL length limits (~2,000 chars on most clients),
 * so we cap message bodies at MAX_BODY_LEN and append "..." if exceeded.
 */
import { toast } from "sonner";

const MAX_BODY_LEN = 1800;

function truncate(s: string, max = MAX_BODY_LEN): string {
  if (s.length <= max) return s;
  return s.slice(0, max - 4) + " ...";
}

// ─── Email ────────────────────────────────────────────────────────

export interface EmailShareOptions {
  subject: string;
  body: string;
  /** Optional recipient. Most users prefer to fill this themselves. */
  to?: string;
}

/**
 * Open the user's email client with subject + body pre-filled.
 *
 * Pair with a CSV/PDF download:
 *   1. Trigger the file download
 *   2. Call shareViaEmail with a summary
 *   3. User opens their email client (which we've opened with mailto:)
 *      and attaches the downloaded file
 */
export function shareViaEmail({ subject, body, to = "" }: EmailShareOptions) {
  const params = new URLSearchParams();
  params.set("subject", subject);
  params.set("body", truncate(body));
  const url = `mailto:${to}?${params.toString()}`;
  window.location.href = url;
}

// ─── WhatsApp ─────────────────────────────────────────────────────

export interface WhatsAppShareOptions {
  text: string;
  /** Optional phone number (no spaces, with country code, no +). */
  phone?: string;
}

/**
 * Open WhatsApp (web or app, depending on platform) with text pre-filled.
 *
 * wa.me handles all platforms — desktop opens web.whatsapp.com, mobile
 * opens the app.
 */
export function shareViaWhatsApp({ text, phone }: WhatsAppShareOptions) {
  const truncated = truncate(text);
  const base = phone ? `https://wa.me/${phone}` : "https://wa.me/";
  const url = `${base}?text=${encodeURIComponent(truncated)}`;
  window.open(url, "_blank", "noopener,noreferrer");
}

// ─── Copy link / text ─────────────────────────────────────────────

/**
 * Copy text to clipboard. Used when the user wants to share manually.
 */
export async function copyToClipboard(text: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  } catch {
    toast.error("Couldn't copy to clipboard");
  }
}
